import { z } from "zod";

import type { Appointment, Definition, ProcedureLog } from "@lib/practice/OpenDentalAdapter";

export const credentialSchema = z.object({
  apiKey: z.string(),
});

type UserParam = {
  userName: string | null;
};

export type Provider = {
  id: number;
  firstName: string;
  lastName: string;
};

export type Operatory = {
  id: number;
  name: string;
};

export type PatientSearchParam = {
  firstName: string;
  lastName: string;
  birthday?: string;
  email?: string;
  phone?: string;
  userName: string | null;
  patientId?: number;
};

export type PatientCreateParam = {
  firstName: string;
  lastName: string;
  birthday: string;
  email: string;
  phone: string;
  userName: string | null;
};

export type PatientInfoParam = {
  patientId: number;
  userName: string | null;
};

export type AppointmentHistory = {
  total: number;
  onTime: number;
  appointmentDate: string;
  provider: string;
  provNum: number;
};

export type LastAppointment = {
  appointmentDate: string | null;
  provider: string | null;
};

export type Patient = {
  patientId: number;
  firstName: string;
  lastName: string;
  birthDate: string;
  email: string;
  phone: string;
  cellPhone: string;
  homePhone: string;
  workPhone: string;
};

export type AvailabilityParam = {
  startDate: string;
  endDate: string;
  providerId: number;
  operatories: number[];
  duration: number;
  blockoutNum: number[];
  availableDays: number[];
  scheduleByHour: boolean;
  userName: string;
};

export type TimeSlot = {
  startTime: string;
  endTime: string;
  providerId: number;
  operatoryId: number;
};

export type CreateAppointmentParam = {
  patientId: number;
  operatoryId: number;
  providerId: number;
  appointmentTime: string;
  duration: number;
  isNewPatient: boolean;
  note: string;
  userName: string | null;
  pattern?: string;
};

export type CheckRealTimeAvailabilityParam = {
  newApptStartTime: string;
  newApptduration: number;
  operatoryId: number;
  userName: string | null;
};

export type ProcConnectParam = {
  appointmentId: number;
  procedures: number[];
  userName: string | null;
};

export type CheckAvailabilityParam = {
  appointmentTime: string;
  operatoryId: number;
  duration: number;
  userName: string | null;
};

export type CollisionFreeResponse = {
  collisionFree: boolean;
};
export type ProcedureTimeParam = {
  codes: Array<{ procCode: string; toothNum: number }>;
  provider: number;
  userName: string;
  ptAge: number;
};

export type Procedure = ProcedureLog & {
  priorityValue: string;
};

export const searchPatient = async (param: PatientSearchParam): Promise<Patient[]> => {
  try {
    const response = await fetch("/api/practice/patients/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return [];
  }
};

export const createPatient = async (param: PatientCreateParam): Promise<Patient> => {
  try {
    const response = await fetch("/api/practice/patients/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getPatientAppointmentHistory = async (param: PatientInfoParam): Promise<AppointmentHistory> => {
  try {
    const response = await fetch("/api/practice/patients/appointments", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};
export const getPatientAppointmentHistoryV2 = async (
  param: PatientInfoParam
): Promise<AppointmentHistory> => {
  try {
    const response = await fetch("/api/practice/patients/lastAppointmentV2", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getPatientPaymentHistory = async (param: PatientInfoParam): Promise<number> => {
  try {
    const response = await fetch("/api/practice/patients/payments", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return 0;
  }
};

export const getPatientLastAppointment = async (param: PatientInfoParam): Promise<LastAppointment> => {
  try {
    const response = await fetch("/api/practice/patients/last-appointment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return {
      appointmentDate: null,
      provider: null,
    };
  }
};

export const getTreatmentPlanProcedures = async (param: PatientInfoParam): Promise<ProcedureLog[]> => {
  try {
    const response = await fetch("/api/practice/patients/procedures", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return [];
  }
};

export const connectProceduresToAppointment = async (param: ProcConnectParam): Promise<void> => {
  try {
    const response = await fetch("/api/practice/appointments/connect", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return;
  }
};

export const getMonthAvailability = async (param: AvailabilityParam): Promise<TimeSlot[]> => {
  try {
    const response = await fetch("/api/practice/appointments/monthslots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const slots = await response.json();
    return Promise.resolve(slots);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getProcedureTimeByProvider = async (
  param: ProcedureTimeParam
): Promise<{ duration: number; blockouts: Array<number> }> => {
  try {
    const response = await fetch("/api/practice/procedure/providerTime", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const result = await response.json();
    return Promise.resolve(result);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getDateAvailability = async (param: AvailabilityParam): Promise<TimeSlot[]> => {
  try {
    const response = await fetch("/api/practice/appointments/dateslots", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    const slots = await response.json();
    return Promise.resolve(slots);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const createAppointment = async (param: CreateAppointmentParam): Promise<Appointment> => {
  try {
    const response = await fetch("/api/practice/appointments/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const checkRealTimeAvailability = async (
  param: CheckRealTimeAvailabilityParam
): Promise<CollisionFreeResponse> => {
  try {
    const response = await fetch("/api/practice/appointments/checkRealTimeAvailability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getProviders = async (param: UserParam): Promise<Provider[]> => {
  try {
    const response = await fetch("/api/practice/providers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });

    const providers = await response.json();
    console.log("PROVs: ", providers);
    return Promise.resolve(providers);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getBlockoutTypes = async (param: UserParam): Promise<Definition[]> => {
  try {
    const response = await fetch("/api/practice/blockoutTypes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });

    const blockouts = await response.json();
    return Promise.resolve(blockouts);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getPriorities = async (param: UserParam): Promise<Definition[]> => {
  try {
    const response = await fetch("/api/practice/priorities", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });

    const priorities = await response.json();
    return Promise.resolve(priorities);
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const getOperatories = async (param: UserParam): Promise<Operatory[]> => {
  try {
    const response = await fetch("/api/practice/operatories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};

export const checkAvailability = async (param: CheckAvailabilityParam): Promise<boolean> => {
  try {
    const response = await fetch("/api/practice/appointments/checkAvailability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(param),
    });
    return await response.json();
  } catch (err) {
    console.log("err", err);
    return Promise.reject(err);
  }
};
